import { FC, useEffect, useState } from "react";
import { ModalA } from "../../common/components/ModalA";
import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AxiosError } from "axios";
import { Asterisk } from "../../common/components/Asterisk";
import { Controller, useForm } from "react-hook-form";
import { ButtonSubmitA } from "../../common/components/ButtonSubmitA";
import { ButtonCancelA } from "../../common/components/ButtonCancelA";
import styled from "@emotion/styled";
import { downloadTaskCounterTemplate } from "../miscellaneous/downloadTaskCounterTemplate";
import { useMutation } from "react-query";
import { fetchImportTaskCounterTemplate } from "../miscellaneous/fetchImportTaskCounterTemplate";
import { fetchGetCompaniesByParent } from "../../organization-management/miscellaneous/fetchGetCompaniesByParent";
import { useNavigate } from "react-router-dom";
import _ from "lodash";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: "200px",
});

const formControlStyle = {
  marginTop: "20px",
  width: "100%",
};

const btnGroupStyle = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  gap: "20px",
  marginTop: "20px",
};

const VALIDATION_ERROR_MESSAGE =
  "Cannot import task counter templates because some data does not meet the required validation rules. Please make sure the entries follow the provided guidance.";

type ImportTaskCounterModalProps = {
  parentCompanies: any;
  companiesName: any;
  isShowImportModal: boolean;
  setIsShowImportModal: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ImportTaskCounterModal: FC<ImportTaskCounterModalProps> = ({
  parentCompanies,
  companiesName,
  isShowImportModal,
  setIsShowImportModal,
}) => {
  const navigate = useNavigate();
  const [textFileName, setTextFileName] = useState("");
  const [isShowToastDanger, setIsShowToastDanger] = useState(false);

  const userRole = !!localStorage.getItem("rolesJson")
    ? JSON.parse(localStorage.getItem("rolesJson") as string)
    : [];

  const isRoleAdmin =
    userRole &&
    userRole.length > 0 &&
    userRole[0]?.Name &&
    userRole[0]?.Name.toLowerCase() === "admin";

  const parentCompanyId = localStorage.getItem("parentCompanyId") ?? "";

  const [localOrganizations, setLocalOrganizations] = useState<any[]>([]);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    clearErrors,
  } = useForm();

  const mutationGetOrganizations = useMutation(fetchGetCompaniesByParent, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const orgs = _.map(res.data.data, (item: any) => {
          return { name: item.companyName, id: item.id };
        });
        setLocalOrganizations(orgs);
      }
    },
    onError: () => {},
  });

  useEffect(() => {
    if (isRoleAdmin && parentCompanyId && isShowImportModal) {
      setValue("parentCompanyId", parentCompanyId);
      mutationGetOrganizations.mutate(parentCompanyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoleAdmin, parentCompanyId, isShowImportModal]);

  const mutateImportTaskCounter = useMutation(fetchImportTaskCounterTemplate, {
    onSuccess: (response) => {
      if (response && response.status === 200) {
        const contentType = response.headers["content-type"];
        if (
          contentType &&
          contentType.includes(
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          )
        ) {
          setIsShowToastDanger(true);
        } else {
          setIsShowImportModal(false);
          navigate("/task-counter", { state: { importSuccess: true } });
        }
      }
    },
    onError: (error: AxiosError) => {
      if (error && error.response && error.response.status === 400) {
        setIsShowToastDanger(true);
      }
    },
  });

  const handleCloseModal = () => {
    setTextFileName("");
    setIsShowImportModal(false);
    reset();
  };

  const handleSubmitImport = (data: any) => {
    if (data.fileName && data.fileName[0]) {
      const payload = {
        parentCompanyId: data.parentCompanyId,
        organizationId: data.organizationId,
        file: data.fileName[0],
      };
      mutateImportTaskCounter.mutate(payload);
    }
  };

  const handleCloseToast = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setIsShowToastDanger(false);
  };

  return (
    <>
      <ModalA
        title="Import from file"
        onCloseModal={handleCloseModal}
        isOpen={isShowImportModal}
        width={600}
      >
        <form onSubmit={handleSubmit(handleSubmitImport)}>
          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">
              Company <Asterisk />
            </label>
            <Controller
              control={control}
              name="parentCompanyId"
              rules={{ required: "Company is required" }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  id="parentCompanyId"
                  value={value ?? ""}
                  onChange={(e) => {
                    setValue("parentCompanyId", e.target.value);
                    clearErrors("parentCompanyId");
                    setValue("organizationId", "");
                    mutationGetOrganizations.mutate(e.target.value as string);
                  }}
                  style={{ height: "38px" }}
                  disabled={isRoleAdmin}
                >
                  {(parentCompanies ?? []).map((item: any) => (
                    <MenuItem key={`import-company-${item.value}`} value={item.value}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            ></Controller>
            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("parentCompanyId").error?.message}
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">
              Organization <Asterisk />
            </label>
            <Controller
              control={control}
              name="organizationId"
              rules={{ required: "Organization is required" }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  id="organizationId"
                  value={value ?? ""}
                  onChange={(e) => {
                    setValue("organizationId", e.target.value);
                    clearErrors("organizationId");
                  }}
                  style={{ height: "38px" }}
                >
                  {(localOrganizations ?? []).map((item: any) => {
                    return (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              )}
            ></Controller>
            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("organizationId").error?.message}
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">
              Upload File{" "}
              <Tooltip
                title={
                  <>
                    <p>The maximum file size allowed is 20MB.</p>
                    <p>Maximum number of rows is 1000.</p>
                    <p>
                      Please use Microsoft Excel Version 2013 or later to enter the
                      data to the Excel Template.
                    </p>
                    <p>Supported format: xls, xlsx.</p>
                  </>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      minInlineSize: "fit-content",
                      backgroundColor: "#fff",
                      color: "#333",
                      border: "1px solid #ccc",
                      fontSize: "13px",
                      lineHeight: 1.4,
                      padding: "8px 12px",
                      "& p": { margin: 0 },
                    },
                  },
                  arrow: {
                    sx: {
                      color: "#fff",
                      "&::before": { border: "1px solid #ccc" },
                    },
                  },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: "18px", color: "#203C77", cursor: "pointer", verticalAlign: "middle" }} />
              </Tooltip>
              {" "}<Asterisk />
            </label>

            <div className="flex w-full gap-2">
              <TextField
                className="w-full"
                value={textFileName ?? ""}
                sx={{
                  input: {
                    height: "5px",
                  },
                }}
              />

              <Button
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                sx={{
                  backgroundColor: "#203C77",
                  color: "#fff",
                  borderRadius: "5px",
                  minWidth: "100px",
                  textTransform: "capitalize",

                  "&:hover": {
                    backgroundColor: "#203C77",
                  },
                }}
              >
                Browse
                <VisuallyHiddenInput
                  type="file"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .xls, .xlsx"
                  {...register("fileName", {
                    required: "You must choose a file upload",
                  })}
                  onChange={(event) => {
                    if (event.target.files) {
                      setTextFileName(event.target.files[0].name);
                      clearErrors("fileName");
                    }
                  }}
                />
              </Button>
            </div>

            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("fileName").error?.message}
            </FormHelperText>

            <div className="text-sm mt-3">
              <p>Please ensure the following before proceeding:</p>
              <ul className="list-disc ml-5 mt-1">
                <li>
                  <strong>Task Name</strong> should not be left blank
                </li>
                <li>
                  <strong>Task Type</strong> should be set to <strong>Label</strong>{" "}
                  or <strong>Task</strong>
                </li>
                <li>
                  <strong>Unit Of Measure</strong> should be set to <strong>Items</strong>{" "}
                  or <strong>Minutes</strong>
                </li>
                <li>
                  Multiple <strong>Job Titles</strong> should be separated by a{" "}
                  <strong>semicolon (;)</strong>
                </li>
              </ul>
            </div>

            <div className="flex justify-start">
              <Button
                onClick={(event) => {
                  event.stopPropagation();
                  downloadTaskCounterTemplate();
                }}
                sx={{
                  textTransform: "capitalize",
                  color: "#203C77",
                  padding: 0,
                  marginTop: "5px",
                }}
              >
                Download Excel Template
              </Button>
            </div>
          </FormControl>

          <FormControl fullWidth sx={btnGroupStyle}>
            <ButtonCancelA name="Cancel" onHandleClick={handleCloseModal} />
            <ButtonSubmitA name="Save" disabled={mutateImportTaskCounter.isLoading} />
          </FormControl>
        </form>
      </ModalA>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={isShowToastDanger}
        autoHideDuration={8000}
        onClose={handleCloseToast}
      >
        <Alert
          onClose={handleCloseToast}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          <p>
            {VALIDATION_ERROR_MESSAGE}
          </p>
        </Alert>
      </Snackbar>
    </>
  );
};
